import RedesimProtocoloService from '@/services/RedesimProtocoloService';

export default {
  namespaced: true,
  state: {
    step: 1,
    validSteps: [false, false, false, false, false],
    newApplication: {
      entity: null,
      applicationAddress: null,
      mailingAddress: null,
      frameworkEntity: null,
      applicationType: null,
      environmentalStudyTypeRequired: null,
      applicationSimplified: {qualify: false},
    },
    applicationTypesAvailable: [],
    // Estado do protocolo REDESIM
    protocoloRedesim: null,           // Dados completos do protocolo carregado
    prefilledFromProtocolo: false,    // Flag indicando se foi pré-preenchido
    lockPrefilledFields: true,        // Flag para bloquear campos pré-preenchidos (default: true)
    protocoloError: null,             // Erro ao carregar protocolo
    protocoloLoading: false,          // Loading state
  },
  mutations: {
    SET_STEP(state, step) {
      state.step = step
    },
    SET_VALID_STEPS(state, value) {
      state.validSteps.splice(state.step - 1, 1, value);
    },
    NEXT_STEP(state) {
      state.step += 1
    },
    PREVIOUS_STEP(state) {
      state.step -= 1
    },
    SET_ENTITY(state, entity) {
      state.newApplication = { ...state.newApplication, entity }
    },
    SET_APPLICATION_ADDRESS(state, applicationAddress) {
      state.newApplication = { ...state.newApplication, applicationAddress };
    },
    SET_MAILING_ADDRESS(state, mailingAddress) {
      state.newApplication = { ...state.newApplication, mailingAddress }
    },
    SET_FRAMEWORK_ENTITY(state, frameworkEntity) {
      state.newApplication = { ...state.newApplication, frameworkEntity }
    },
    SET_APPLICATION_TYPE(state, applicationTypePayload) {
      state.newApplication = { ...state.newApplication, applicationType: applicationTypePayload }
    },
    SET_APPLICATION_TYPES_AVAILABLE(state, applicatinoTypesAvailable) {
      state.applicationTypesAvailable = applicatinoTypesAvailable
    },
    SET_ENVIRONMENTAL_STUDY_TYPE_REQUIRED(state, environmentalStudyTypeRequired) {
      state.newApplication = { ...state.newApplication, environmentalStudyTypeRequired }
    },
    SET_APPLICATION_SIMPLIFIED(state, applicationSimplified) {
      state.newApplication = { ...state.newApplication, applicationSimplified }
    },
    // Mutations do protocolo REDESIM
    SET_PROTOCOLO_REDESIM(state, data) {
      state.protocoloRedesim = data;
    },
    SET_PREFILLED_FROM_PROTOCOLO(state, value) {
      state.prefilledFromProtocolo = value;
    },
    SET_LOCK_PREFILLED_FIELDS(state, value) {
      state.lockPrefilledFields = value;
    },
    SET_PROTOCOLO_ERROR(state, error) {
      state.protocoloError = error;
    },
    SET_PROTOCOLO_LOADING(state, loading) {
      state.protocoloLoading = loading;
    },
    CLEAR_PROTOCOLO_DATA(state) {
      state.protocoloRedesim = null;
      state.prefilledFromProtocolo = false;
      state.protocoloError = null;
      state.protocoloLoading = false;
    }
  },
  actions: {
    setStep({ commit }, step) {
      commit('SET_STEP', step)
    },
    setValidSteps({ commit }, value) {
      commit('SET_VALID_STEPS', value)
    },
    nextStep({ commit }) {
      commit('NEXT_STEP')
    },
    previousStep({ commit }) {
      commit('PREVIOUS_STEP')
    },
    setEntity({ commit }, entity) {
      commit('SET_ENTITY', entity)
    },
    setApplicationAddress({ commit }, applicationAddress) {
      commit('SET_APPLICATION_ADDRESS', applicationAddress)
    },
    setMailingAddress({ commit }, mailingAddress) {
      commit('SET_MAILING_ADDRESS', mailingAddress)
    },
    setFrameworkEntity({ commit }, frameworkEntity) {
      commit('SET_FRAMEWORK_ENTITY', frameworkEntity)
    },
    setApplicationTypesAvailable({ commit }, applicationTypesAvailable) {
      commit('SET_APPLICATION_TYPES_AVAILABLE', applicationTypesAvailable)
    },
    setApplicationType({ commit }, applicationTypePayload) {
      commit('SET_APPLICATION_TYPE', applicationTypePayload)
    },
    setEnvironmentalStudyTypeRequired({ commit }, environmentalStudyTypeRequired) {
      commit('SET_ENVIRONMENTAL_STUDY_TYPE_REQUIRED', environmentalStudyTypeRequired)
    },
    setApplicationSimplified({ commit }, applicationSimplified) {
      commit('SET_APPLICATION_SIMPLIFIED', applicationSimplified)
    },
    // Actions do protocolo REDESIM
    async loadFromProtocolo({ commit }, protocolo) {
      commit('SET_PROTOCOLO_LOADING', true);
      commit('SET_PROTOCOLO_ERROR', null);

      try {
        const response = await RedesimProtocoloService.fetchByProtocolo(protocolo);
        const data = response.data;

        // Armazena dados completos do protocolo
        commit('SET_PROTOCOLO_REDESIM', data);

        // Pré-preenche a entidade (pessoa) com seu endereço
        // O address retornado pela API é o endereço do solicitante (person.address)
        if (data.person) {
          commit('SET_ENTITY', {
            id: data.person.id,
            cpf_cnpj: data.person.cpf_cnpj,
            name: data.person.name,
            email: data.person.email,
            person_type: data.person.person_type,
            enquadramento: data.person.enquadramento,
            address: data.address, // Endereço do solicitante
          });
        }

        // Pré-preenche o endereço do empreendimento (inicialmente igual ao do solicitante)
        // O usuário pode alterar se o empreendimento for em local diferente
        if (data.address) {
          commit('SET_APPLICATION_ADDRESS', {
            zip_code: data.address.zip_code,
            street: data.address.street,
            number: data.address.number,
            complement: data.address.complement,
            neighborhood: data.address.neighborhood,
            latitude: data.address.latitude,
            longitude: data.address.longitude,
            city: data.address.city,
            federal_unit: data.address.federal_unit,
          });
        }

        // Marca como pré-preenchido
        commit('SET_PREFILLED_FROM_PROTOCOLO', true);
        commit('SET_PROTOCOLO_LOADING', false);

        return { success: true, data };
      } catch (error) {
        const errorMessage = error.response &&  error.response.data && error.response.data.error || 'Erro ao carregar dados do protocolo';
        commit('SET_PROTOCOLO_ERROR', errorMessage);
        commit('SET_PROTOCOLO_LOADING', false);

        return { success: false, error: errorMessage };
      }
    },
    setLockPrefilledFields({ commit }, value) {
      commit('SET_LOCK_PREFILLED_FIELDS', value);
    },
    clearProtocoloData({ commit }) {
      commit('CLEAR_PROTOCOLO_DATA');
    }
  },
  getters: {
    step: state => state.step,
    validSteps: state => state.validSteps,
    newApplication: state => state.newApplication,
    applicationTypesAvailable: state => state.applicationTypesAvailable,
    applicationSimplified: state => state.newApplication.applicationSimplified,
    frameworkEntity: state => state.newApplication.frameworkEntity,
    // Getters do protocolo REDESIM
    protocoloRedesim: state => state.protocoloRedesim,
    prefilledFromProtocolo: state => state.prefilledFromProtocolo,
    lockPrefilledFields: state => state.lockPrefilledFields,
    protocoloError: state => state.protocoloError,
    protocoloLoading: state => state.protocoloLoading,
  }
};
